import React, { forwardRef, type ForwardedRef } from 'react';
import { clsx } from 'clsx';
import './ComboBox.scss';

export interface ESSComboBoxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label for the combo box */
  label?: string;
  /** Helper text to display below the combo box */
  helperText?: string;
  /** Whether the combo box is in an error state */
  isInvalid?: boolean;
  /** Error message to display when isInvalid is true */
  errorMessage?: string;
  /** Size of the combo box */
  size?: 'sm' | 'md' | 'lg';
  /** Options for the combo box */
  options?: Array<{ value: string; label: string }>;
}

/**
 * ESS ComboBox Component - Simplified version
 * Note: This is a simplified implementation. For full ComboBox functionality,
 * use @plone/components ComboBox directly with ESS styling.
 */
export const ComboBox = forwardRef<HTMLInputElement, ESSComboBoxProps>(
  function ComboBox(
    {
      label,
      helperText,
      isInvalid,
      errorMessage,
      size = 'md',
      className,
      options = [],
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const comboBoxClasses = clsx(
      'ess-combobox',
      `ess-combobox--${size}`,
      {
        'ess-combobox--invalid': isInvalid,
      },
      className,
    );

    return (
      <div className={comboBoxClasses}>
        {label && <label className="ess-combobox__label">{label}</label>}
        <div className="ess-combobox__input-wrapper">
          <input
            ref={ref}
            className="ess-combobox__input"
            list={`combobox-${label}`}
            aria-invalid={isInvalid}
            {...props}
          />
          {options.length > 0 && (
            <datalist id={`combobox-${label}`}>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </datalist>
          )}
        </div>
        {(helperText || (isInvalid && errorMessage)) && (
          <div className="ess-combobox__help-text">
            {isInvalid && errorMessage ? errorMessage : helperText}
          </div>
        )}
      </div>
    );
  },
);

ComboBox.displayName = 'ESSComboBox';

// Simplified ComboBoxItem - just an alias for option
export const ComboBoxItem = ({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}) => null;
export type ESSComboBoxItemProps = { value: string; children: React.ReactNode };
